import React, { useEffect, useRef, useState } from 'react';
import { SafeHTML } from '../safe-html';
/**
 * Tells DOMPurify to preserve SVG markup (elements plus presentation attributes
 * such as `fill`, `stroke`, `viewBox`). Verified to keep
 * `fill="var(--rd-sys-color-*)"` values intact, which is what lets an inlined
 * illustration adapt to the active (light/dark) theme.
 */
const SVG_SANITIZE_CONFIG = {
    USE_PROFILES: { svg: true, svgFilters: true },
};
// Fetch each unique SVG only once across the app, and reuse the markup on
// subsequent renders so there is no repeated network cost or flash.
const svgMarkupCache = new Map();
const fetchSvgMarkup = (path) => {
    // No fetch in SSR / some test environments — render nothing rather than
    // throwing. Callers that mock fetch will still resolve normally.
    if (typeof fetch === 'undefined') {
        return Promise.resolve('');
    }
    if (!svgMarkupCache.has(path)) {
        svgMarkupCache.set(path, fetch(path)
            .then((response) => (response.ok ? response.text() : ''))
            .catch(() => ''));
    }
    return svgMarkupCache.get(path);
};
/**
 * Page-wide instance counter. Module federation can load more than one build
 * of radiant-react into a document, and a module-local counter would restart
 * at 1 in each of them and hand out the same suffix twice.
 */
const INSTANCE_COUNTER_KEY = '__rdInlineSvgInstanceCount';
const nextInstanceSuffix = () => {
    var _a;
    const store = globalThis;
    const next = ((_a = store[INSTANCE_COUNTER_KEY]) !== null && _a !== void 0 ? _a : 0) + 1;
    store[INSTANCE_COUNTER_KEY] = next;
    return `i${next}`;
};
const escapeRegExp = (value) => value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
/**
 * Suffix every id the markup declares, and every reference to it, with
 * `-${suffix}`.
 *
 * Inlining lifts an asset's ids into the document's single id space, so two
 * copies of one illustration declare the same ids and `url(#id)` resolves to
 * whichever copy comes first in the document instead of to its own `<svg>`.
 * A copy whose definitions live in a `display: none` subtree paints nothing,
 * so the visible copy then renders with parts of the artwork missing.
 *
 * Only declared ids are rewritten, leaving references to host-page ids alone,
 * and each pattern anchors on the reference's closing delimiter so `mask0` is
 * never rewritten inside `mask0a`.
 */
export const scopeSvgIds = (markup, suffix) => {
    const declaredIds = Array.from(new Set(Array.from(markup.matchAll(/\bid="([^"]+)"/g), (m) => m[1])));
    if (declaredIds.length === 0) {
        return markup;
    }
    const anyDeclaredId = declaredIds.map(escapeRegExp).join('|');
    const scope = (id) => `${id}-${suffix}`;
    /* Sources are built from escapeRegExp'd ids, hence the rule waiver. */
    /* eslint-disable security/detect-non-literal-regexp */
    return markup
        .replace(new RegExp(`\\bid="(${anyDeclaredId})"`, 'g'), (_match, id) => `id="${scope(id)}"`)
        .replace(new RegExp(`url\\((['"]?)#(${anyDeclaredId})\\1\\)`, 'g'), (_match, quote, id) => `url(${quote}#${scope(id)}${quote})`)
        .replace(new RegExp(`((?:xlink:)?href)="#(${anyDeclaredId})"`, 'g'), (_match, attribute, id) => `${attribute}="#${scope(id)}"`);
    /* eslint-enable security/detect-non-literal-regexp */
};
/**
 * Fetches an SVG file and renders its markup inline in the document, instead of
 * via an `<img src>` tag. Inlining is required so that CSS custom properties
 * referenced inside the SVG (e.g. `--rd-sys-color-content-tertiary`) resolve
 * against the host page and follow the active theme — an `<img>`-embedded SVG
 * is CSS-isolated and cannot read them.
 */
export const InlineSvg = ({ path, className }) => {
    const [markup, setMarkup] = useState('');
    // React 17 here, so no useId.
    const instanceSuffix = useRef();
    if (!instanceSuffix.current) {
        instanceSuffix.current = nextInstanceSuffix();
    }
    useEffect(() => {
        let isMounted = true;
        // Reset while the (possibly different) path loads so we never show a
        // stale illustration.
        setMarkup('');
        if (!path) {
            return undefined;
        }
        fetchSvgMarkup(path)
            .then((text) => {
            if (isMounted) {
                // The cache is shared, so scope per instance on the way in.
                setMarkup(scopeSvgIds(text, instanceSuffix.current));
            }
            return text;
        })
            .catch(() => {
            // fetchSvgMarkup already swallows fetch errors (resolves to
            // ''); nothing to recover from here.
        });
        return () => {
            isMounted = false;
        };
    }, [path]);
    // While the fetch is pending, SafeHTML renders an empty (but classed, hence
    // sized) wrapper, avoiding layout shift.
    return (React.createElement(SafeHTML, { className: className, html: markup, config: SVG_SANITIZE_CONFIG }));
};
//# sourceMappingURL=inline-svg.js.map